import { useEffect, useMemo, useState } from 'react';
import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@components/Button';
import Modal from '@components/Modal';
import { supabase } from '@lib/supabase';
import { useAuth } from '@lib/useAuth';
// showErrorToast/showSuccessToast removed from Profile (unused)
import {
  fetchAllBadgesWithCategories,
  fetchUserBadgesWithDetails,
  getUserProfileBadgeColor,
  groupBadgesByCategory,
  getBadgeIcon,
  type BadgeWithCategory,
  type UserBadgeWithDetails,
} from '@lib/badges';

interface ProfileSettingsRecord {
  user_id: string;
  display_name: string | null;
  profile_name: string | null;
  profile_bio: string | null;
  display_profile: boolean | null;
  profile_picture_name: string | null;
  profile_picture_generated_at: string | null;
  created_at: string | null;
}

interface UserStatsRecord {
  id: string;
  full_name: string | null;
  email: string | null;
  total_sessions: number | null;
  total_duration: number | null;
  total_cycles: number | null;
  average_session_duration: number | null;
  longest_session_duration: number | null;
  current_streak: number | null;
  best_streak: number | null;
  last_session_date: string | null;
  rank: number | null;
}

interface StatCardItem {
  label: string;
  value: string;
  helper: string;
  icon?: string;
}

interface HighlightItem extends StatCardItem {}

const formatDateLabel = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDurationFromSeconds = (input?: number | null) => {
  const seconds = Number(input);
  if (!Number.isFinite(seconds) || seconds <= 0) return '0m';
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const remainingSeconds = Math.round(seconds % 60);

  if (hours > 0) {
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }

  if (minutes > 0) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }

  return `${remainingSeconds}s`;
};

const formatStreak = (value?: number | null) => {
  const days = Number(value) || 0;
  return `${days} day${days === 1 ? '' : 's'}`;
};

const buildAvatarUrl = (value?: string | null) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (trimmed.startsWith('http')) {
    return trimmed;
  }
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').replace(
    /\/$/,
    ''
  );
  const normalizedPath = trimmed.replace(/^\/+/, '');
  return `${supabaseUrl}/storage/v1/object/public/${encodeURI(normalizedPath)}`;
};

function Profile() {
  const navigate = useNavigate();
  const { profileName } = useParams<{ profileName?: string }>();
  const { user, user_settings, loading: authLoading } = useAuth();
  const [profileSettings, setProfileSettings] =
    useState<ProfileSettingsRecord | null>(null);
  const [stats, setStats] = useState<UserStatsRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [allBadges, setAllBadges] = useState<BadgeWithCategory[]>([]);
  const [userBadges, setUserBadges] = useState<UserBadgeWithDetails[]>([]);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [sidebarColor, setSidebarColor] = useState<string | null>(null);
  const [sidebarTextColor, setSidebarTextColor] = useState<string | null>(null);
  // rightSidebarColor used to be stored separately for a right-column
  // style but the value was never consumed; keep a single sidebarColor
  // state instead to avoid an unused variable warning.
  const [badgeTab, setBadgeTab] = useState<'all' | 'unlocked'>('unlocked');
  const [badgeModalOpen, setBadgeModalOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<BadgeWithCategory | null>(
    null
  );
  const [selectedUserBadge, setSelectedUserBadge] =
    useState<UserBadgeWithDetails | null>(null);
  const badgeRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;

    async function loadProfile() {
      setLoading(true);
      setSidebarColor(null);
      setSidebarTextColor(null);
      setError(null);

      try {
        let settingsRecord: ProfileSettingsRecord | null = null;
        let targetUserId: string | null = null;
        let ownProfile = false;

        if (profileName) {
          const slug = profileName.trim();
          if (!slug) {
            throw new Error('Profile not found.');
          }
          const { data, error: settingsError } = await supabase
            .from('user_settings')
            .select(
              'user_id, display_name, profile_name, profile_bio, display_profile, profile_picture_name, profile_picture_generated_at, created_at'
            )
            .ilike('profile_name', slug)
            .limit(1)
            .single();

          if (settingsError) {
            if ((settingsError as { code?: string }).code === 'PGRST116') {
              throw new Error('Profile not found.');
            }
            throw settingsError;
          }

          ownProfile = user?.id === data.user_id;
          if (!data.display_profile && !ownProfile) {
            throw new Error('This profile is private.');
          }

          settingsRecord = data as ProfileSettingsRecord;
          targetUserId = data.user_id;
        } else if (user) {
          settingsRecord = {
            user_id: user.id,
            display_name:
              user_settings?.display_name ||
              user.user_metadata?.name ||
              user.email ||
              null,
            profile_name: user_settings?.profile_name || null,
            profile_bio: user_settings?.profile_bio || null,
            display_profile: user_settings?.display_profile ?? null,
            profile_picture_name: user_settings?.profile_picture_name || null,
            profile_picture_generated_at:
              user_settings?.profile_picture_generated_at || null,
            created_at: user.created_at || null,
          };
          targetUserId = user.id;
          ownProfile = true;
        } else {
          setError('Sign in to see your profile.');
          setProfileSettings(null);
          setStats(null);
          setIsOwnProfile(false);
          setLoading(false);
          return;
        }

        let statsRecord: UserStatsRecord | null = null;
        if (targetUserId) {
          const { data: statsData, error: statsError } = await supabase
            .from('user_statistics')
            .select(
              'id, full_name, email, total_sessions, total_duration, total_cycles, average_session_duration, longest_session_duration, current_streak, best_streak, last_session_date, rank'
            )
            .eq('id', targetUserId)
            .limit(1)
            .single();

          if (statsError && statsError.code !== 'PGRST116') {
            throw statsError;
          }

          statsRecord = (statsData as UserStatsRecord) ?? null;
        }

        if (cancelled) return;
        setProfileSettings(settingsRecord);
        setStats(statsRecord);
        setIsOwnProfile(ownProfile);

        // Load badges if we have a target user
        if (targetUserId) {
          setBadgesLoading(true);
          try {
            const [badges, earnedBadges] = await Promise.all([
              fetchAllBadgesWithCategories(),
              fetchUserBadgesWithDetails(targetUserId),
            ]);
            if (!cancelled) {
              setAllBadges(badges);
              setUserBadges(earnedBadges);
            }

            // Determine profile sidebar color from the user's highest badge.
            // Try the DB helper; if that returns null and we have detailed
            // userBadges already, compute locally as a fallback.
            try {
              const highest = await getUserProfileBadgeColor(targetUserId);
              if (!cancelled && highest) {
                if (highest.color) {
                  setSidebarColor(highest.color);
                }
                // Always set sidebarTextColor to white when highest.value is non-zero
                if (highest.value && highest.value !== 0) {
                  setSidebarTextColor('#ffffff');
                }
              } else if (
                !cancelled &&
                earnedBadges &&
                earnedBadges.length > 0
              ) {
                // Local fallback — prefer level then score
                let best: { color?: string | null; value: number } | null =
                  null;
                for (const ub of earnedBadges) {
                  const b = ub?.badge as BadgeWithCategory | undefined;
                  if (!b) continue;
                  let levelVal = Number.NaN;
                  if (b && 'level' in b) {
                    const lv = (b as unknown as { level?: unknown }).level;
                    const parsed = Number(lv);
                    if (Number.isFinite(parsed)) levelVal = parsed;
                  }
                  const scoreVal = Number.isFinite(Number(b.score))
                    ? Number(b.score)
                    : Number.NaN;
                  const weight = !Number.isNaN(levelVal)
                    ? levelVal
                    : !Number.isNaN(scoreVal)
                      ? scoreVal
                      : 0;
                  if (!best || weight > best.value) {
                    best = { color: b.color ?? null, value: weight };
                  }
                }
                if (best) {
                  if (best.color) {
                    const c = String(best.color).trim();
                    setSidebarColor(c);
                  }
                  if (best.value && best.value !== 0)
                    setSidebarTextColor('#ffffff');
                }
              }
            } catch (err) {
              console.debug('Failed to resolve sidebar color', err);
            }

            // If we're viewing someone else's profile and the direct
            // user_badges query returned nothing (e.g. blocked by RLS), try
            // our server-side endpoint which runs with the service role.
            if (
              !cancelled &&
              !ownProfile &&
              (!earnedBadges || earnedBadges.length === 0)
            ) {
              try {
                const resp = await fetch(
                  `/api/public-user-badges?user_id=${encodeURIComponent(targetUserId)}`
                );
                if (resp.ok) {
                  const parsed = await resp.json();
                  // API returns either an array (legacy) or an object { badges, highest_score }
                  const publicBadges = Array.isArray(parsed)
                    ? (parsed as UserBadgeWithDetails[])
                    : ((parsed?.badges as UserBadgeWithDetails[] | undefined) ??
                      []);
                  if (!cancelled && publicBadges && publicBadges.length > 0) {
                    setUserBadges(publicBadges);
                  }
                }
              } catch (err) {
                // ignore; public endpoint is best-effort
                console.debug('public-user-badges fetch failed', err);
              }
            }
          } catch (badgeErr) {
            console.error('Failed to load badges:', badgeErr);
          } finally {
            if (!cancelled) {
              setBadgesLoading(false);
            }
          }
        }
      } catch (err: unknown) {
        if (cancelled) return;
        console.error('Failed to load profile', err);
        const message = err instanceof Error ? err.message : String(err ?? '');
        setError(message || 'Unable to load this profile.');
        setProfileSettings(null);
        setStats(null);
        setIsOwnProfile(false);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    profileName,
    user,
    user?.id,
    user?.created_at,
    user?.email,
    user?.user_metadata?.name,
    user_settings?.display_name,
    user_settings?.profile_name,
    user_settings?.profile_bio,
    user_settings?.display_profile,
    user_settings?.profile_picture_name,
    user_settings?.profile_picture_generated_at,
  ]);

  const avatarUrl = useMemo(
    () => buildAvatarUrl(profileSettings?.profile_picture_name),
    [profileSettings?.profile_picture_name]
  );

  const badgesByCategory = useMemo(
    () => groupBadgesByCategory(allBadges),
    [allBadges]
  );

  const orderedBadgeGroups = useMemo(
    () =>
      Array.from(badgesByCategory.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(
          ([categoryName, badges]) =>
            [
              categoryName,
              [...badges].sort(
                (a, b) =>
                  (a.requirement_value ?? 0) - (b.requirement_value ?? 0)
              ),
            ] as [string, BadgeWithCategory[]]
        ),
    [badgesByCategory]
  );

  const earnedBadgeIds = useMemo(
    () => new Set(userBadges.map((ub) => ub.badge_id)),
    [userBadges]
  );

  // Highest-scoring badge for this user (based on badge.score)
  const topScoreBadge = useMemo(() => {
    if (!userBadges || userBadges.length === 0) return null;
    let best: UserBadgeWithDetails | null = null;
    for (const ub of userBadges) {
      const b = ub?.badge as BadgeWithCategory | undefined;
      if (!b) continue;
      const score = Number.isFinite(Number(b.score)) ? Number(b.score) : 0;
      const bestScore = best?.badge ? Number(best.badge.score ?? 0) : -Infinity;
      if (best === null || score > bestScore) best = ub;
    }
    return best ?? null;
  }, [userBadges]);

  const hasBadges = userBadges.length > 0;

  // Calm score removed temporarily.

  const displayName =
    profileSettings?.display_name?.trim() ||
    stats?.full_name ||
    profileSettings?.profile_name ||
    'Breather';

  const memberSince = useMemo(() => {
    if (isOwnProfile) {
      return formatDateLabel(user?.created_at || profileSettings?.created_at);
    }
    return formatDateLabel(profileSettings?.created_at);
  }, [isOwnProfile, user?.created_at, profileSettings?.created_at]);

  const profileSlug = profileSettings?.profile_name || null;

  const statCards = useMemo(
    (): StatCardItem[] => [
      {
        label: 'Total Sessions',
        icon: '🧘',
        value: stats?.total_sessions
          ? stats.total_sessions.toLocaleString()
          : '0',
        helper: '',
      },
      {
        label: 'Minutes Practiced',
        icon: '⏱️',
        value: formatDurationFromSeconds(stats?.total_duration),
        helper: '',
      },
      {
        label: 'Current Streak',
        icon: '⏳',
        value: formatStreak(stats?.current_streak),
        helper: '',
      },
      {
        label: 'Best Streak',
        icon: '⏳',
        value: formatStreak(stats?.best_streak),
        helper: '',
      },
    ],
    [stats]
  );

  const extendedHighlights = useMemo(
    (): HighlightItem[] => [
      {
        label: 'Longest Session',
        icon: '⏱️',
        value: formatDurationFromSeconds(stats?.longest_session_duration),
        helper: '',
      },
      {
        label: 'Avg. Session Length',
        icon: '⏱️',
        value: formatDurationFromSeconds(stats?.average_session_duration),
        helper: '',
      },
      {
        label: 'Total Cycles',
        icon: '🔁',
        value: stats?.total_cycles ? stats.total_cycles.toLocaleString() : '0',
        helper: '',
      },
    ],
    [stats]
  );

  const showStats = Boolean(stats);

  return (
    <div className="animate-fade-in mx-auto max-w-5xl py-12">
      {loading ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-10 text-center text-gray-600">
          Loading profile...
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50/80 px-6 py-4 text-center text-rose-700">
          {error}
        </div>
      ) : !profileSettings ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-10 text-center text-gray-600">
          No profile details available yet.
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-3">
          <div
            className="card lg:col-span-1"
            style={
              sidebarColor
                ? {
                    backgroundColor: sidebarColor,
                    color: sidebarTextColor ?? '#ffffff',
                  }
                : undefined
            }
          >
            <div className="mb-4 flex flex-col items-center justify-center">
              {avatarUrl ? (
                <a
                  href={avatarUrl}
                  onClick={(e) => {
                    // Allow default link behavior for modifiers (open in new tab/window)
                    if (
                      e.button !== 0 ||
                      e.ctrlKey ||
                      e.metaKey ||
                      e.shiftKey ||
                      e.altKey
                    ) {
                      return;
                    }

                    e.preventDefault();
                    setAvatarModalOpen(true);
                  }}
                  className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                  style={
                    sidebarTextColor ? { color: sidebarTextColor } : undefined
                  }
                  aria-label="View profile picture"
                >
                  <img
                    src={avatarUrl}
                    alt={displayName}
                    className="h-24 w-24 rounded-full object-cover shadow-lg ring-2 ring-white/80"
                  />
                </a>
              ) : (
                <>
                  <div
                    className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 text-white"
                    style={
                      sidebarTextColor ? { color: sidebarTextColor } : undefined
                    }
                  >
                    <span aria-hidden="true" className="text-6xl">
                      😮‍💨
                    </span>
                    <span className="sr-only">{displayName}</span>
                  </div>
                  {/* calm score display removed */}
                </>
              )}
              {isOwnProfile && !profileSettings?.profile_picture_name && (
                <p className="mt-2 text-center text-xs">
                  <button
                    type="button"
                    onClick={() =>
                      navigate({
                        pathname: '/settings',
                        search: '?tab=profile',
                      })
                    }
                    className="font-semibold text-primary-600 underline decoration-primary-200 underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                  >
                    Create AI Profile Picture
                  </button>
                </p>
              )}
            </div>
            <h2
              className="mb-1 text-center text-2xl font-semibold"
              style={sidebarTextColor ? { color: sidebarTextColor } : undefined}
            >
              {displayName}
            </h2>
            {profileSlug && (
              <p
                className="text-center text-sm"
                style={
                  sidebarTextColor ? { color: sidebarTextColor } : undefined
                }
              >
                @{profileSlug}
              </p>
            )}
            {isOwnProfile && profileSettings.display_profile === false && (
              <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-center text-xs font-semibold text-amber-700">
                Your profile is hidden. Turn it on in Settings to share this
                page.
              </p>
            )}
            <div
              className="mt-6 space-y-2 text-center text-sm"
              style={sidebarTextColor ? { color: sidebarTextColor } : undefined}
            >
              {memberSince && (
                <p>
                  <span
                    className="font-semibold"
                    style={
                      sidebarTextColor ? { color: sidebarTextColor } : undefined
                    }
                  >
                    Member since:
                  </span>{' '}
                  {memberSince}
                </p>
              )}
            </div>
            {profileSettings.profile_bio && (
              <p
                className="mt-4 whitespace-pre-line text-center text-sm"
                style={
                  sidebarTextColor ? { color: sidebarTextColor } : undefined
                }
              >
                {profileSettings.profile_bio}
              </p>
            )}
            {/* Rank will be rendered below the Edit Profile button for the owner; for non-owners show it here */}
            {!isOwnProfile && (stats?.rank || stats) ? (
              <div className="mt-2 text-center">
                {stats?.rank ? (
                  <div
                    className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm font-semibold"
                    style={sidebarColor ? { color: sidebarColor } : undefined}
                  >
                    <span aria-hidden>🏆</span>
                    <span>Ranked</span>
                    <span className="ml-1 text-lg">#{stats.rank}</span>
                  </div>
                ) : (
                  <div
                    className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-2 text-sm font-medium"
                    style={
                      sidebarTextColor ? { color: sidebarTextColor } : undefined
                    }
                  >
                    <span aria-hidden>—</span>
                    <span>Unranked</span>
                  </div>
                )}
              </div>
            ) : null}
            {isOwnProfile && (
              <div className="mt-6">
                <Button
                  variant="primary"
                  size="md"
                  className="item-selected w-full"
                  onClick={() =>
                    navigate({ pathname: '/settings', search: '?tab=profile' })
                  }
                >
                  Edit Profile
                </Button>
              </div>
            )}
            {isOwnProfile && (stats?.rank || stats) ? (
              <div className="mt-4 text-center">
                {stats?.rank ? (
                  <div
                    className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm font-semibold"
                    style={sidebarColor ? { color: sidebarColor } : undefined}
                  >
                    <span aria-hidden>🏆</span>
                    <span>Ranked</span>
                    <span className="ml-1 text-lg">#{stats.rank}</span>
                  </div>
                ) : (
                  <div
                    className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-3 py-2 text-sm font-medium"
                    style={
                      sidebarTextColor ? { color: sidebarTextColor } : undefined
                    }
                  >
                    <span aria-hidden>—</span>
                    <span>Unranked</span>
                  </div>
                )}
              </div>
            ) : null}
            {topScoreBadge && topScoreBadge.badge?.image_url ? (
              <div className="mt-4 flex flex-col items-center justify-center pt-10">
                <div className="relative h-48 w-48">
                  <img
                    src={
                      buildAvatarUrl(topScoreBadge.badge.image_url) || undefined
                    }
                    alt={topScoreBadge.badge.name || 'Top badge'}
                    className="h-48 w-48 rounded-md object-cover shadow-sm"
                  />
                  <div className="pointer-events-none absolute inset-x-0 top-2 flex justify-center">
                    <span className="rounded-md bg-black/60 px-2 py-1 text-xs font-semibold text-white">
                      Top Achievement Badge
                    </span>
                  </div>
                </div>
                {/* calm score display removed */}
              </div>
            ) : null}
          </div>

          <div className="space-y-6 lg:col-span-2">
            <section className="card">
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Practice Statistics
                </h3>
                {/* Rank moved to profile sidebar for better visibility */}
              </div>
              {showStats ? (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {statCards.map((card) => (
                      <div
                        key={card.label}
                        className="rounded-2xl p-4 shadow-inner"
                        style={
                          sidebarColor
                            ? {
                                backgroundColor: '#e7f5f6',
                                color: '#000000',
                              }
                            : undefined
                        }
                      >
                        <p className="text-xs uppercase tracking-wide text-black">
                          <span
                            aria-hidden
                            className="mr-2 align-middle text-sm"
                          >
                            {card.icon ?? ''}
                          </span>
                          {card.label}
                        </p>
                        <p className="mt-1 text-2xl font-semibold text-black">
                          {card.value}
                        </p>
                        <p className="text-sm text-black">{card.helper}</p>
                      </div>
                    ))}
                  </div>
                  {isOwnProfile && (
                    <div className="mt-6 flex justify-center">
                      <div className="flex justify-center pt-4">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate('/stats')}
                          className="item-selected w-full sm:w-auto"
                        >
                          View All Stats
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-8 text-center text-sm text-gray-600">
                  No public sessions yet. Complete a leaderboard-eligible
                  session to unlock your stats.
                </div>
              )}
            </section>

            <section className="card">
              <h3 className="mb-4 text-xl font-semibold text-gray-900">
                Highlights
              </h3>
              {showStats ? (
                <div className="grid gap-4 sm:grid-cols-3">
                  {extendedHighlights.map((item) => (
                    <div
                      key={item.label}
                      className="rounded-2xl p-4 shadow-inner"
                      style={
                        sidebarColor
                          ? {
                              backgroundColor: '#e7f5f6',
                              color: '#000000',
                            }
                          : undefined
                      }
                    >
                      <p className="text-xs uppercase tracking-wide text-black">
                        <span aria-hidden className="mr-2 align-middle text-sm">
                          {item.icon ?? ''}
                        </span>
                        {item.label}
                      </p>
                      <p className="mt-2 text-xl font-semibold text-black">
                        {item.value}
                      </p>
                      <p className="mt-1 text-sm text-black">{item.helper}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-8 text-center text-sm text-gray-600">
                  Highlights will appear after your first public session.
                </div>
              )}
            </section>

            <section className="card">
              <h3 className="mb-2 text-xl font-semibold text-gray-900">
                Achievements
              </h3>
              {isOwnProfile && (
                <div className="mb-3 flex items-center justify-start gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={badgeTab === 'unlocked' ? 'primary' : 'secondary'}
                    className={badgeTab === 'unlocked' ? 'item-selected' : ''}
                    onClick={() => setBadgeTab('unlocked')}
                  >
                    Unlocked
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={badgeTab === 'all' ? 'primary' : 'secondary'}
                    className={badgeTab === 'all' ? 'item-selected' : ''}
                    onClick={() => setBadgeTab('all')}
                  >
                    All
                  </Button>
                </div>
              )}
              {badgesLoading ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-8 text-center text-sm text-gray-600">
                  Loading badges...
                </div>
              ) : isOwnProfile ? (
                badgeTab === 'all' ? (
                  // Owner: full catalog (earned + locked)
                  allBadges.length > 0 || hasBadges ? (
                    <div className="space-y-6">
                      {orderedBadgeGroups.map(
                        ([categoryName, badges]: [
                          string,
                          BadgeWithCategory[],
                        ]) => {
                          const earnedInCategory = badges.filter((badge) =>
                            earnedBadgeIds.has(badge.id)
                          );
                          const lockedInCategory = badges.filter(
                            (badge) => !earnedBadgeIds.has(badge.id)
                          );

                          return (
                            <div key={categoryName}>
                              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                                {categoryName}
                              </h4>
                              <div className="grid gap-3 sm:grid-cols-2">
                                {earnedInCategory.map((badge) => {
                                  const userBadge = userBadges.find(
                                    (ub) => ub.badge_id === badge.id
                                  );
                                  const earnedDate = userBadge?.earned_at
                                    ? formatDateLabel(userBadge.earned_at)
                                    : null;

                                  return (
                                    <button
                                      key={badge.id}
                                      type="button"
                                      onClick={() => {
                                        setSelectedBadge(badge);
                                        setSelectedUserBadge(userBadge ?? null);
                                        setBadgeModalOpen(true);
                                      }}
                                      className="flex transform items-start gap-3 rounded-xl border border-l-4 bg-white p-4 text-left shadow-lg ring-1 ring-gray-100 transition hover:scale-[1.02]"
                                      ref={(el) => {
                                        if (el) {
                                          try {
                                            el.style.setProperty(
                                              'border-left-color',
                                              badge.color ?? '',
                                              'important'
                                            );
                                            el.style.setProperty(
                                              'border-color',
                                              badge.color ?? '',
                                              'important'
                                            );
                                            el.style.setProperty(
                                              'background-color',
                                              badge.color ?? '',
                                              'important'
                                            );
                                          } catch (e) {
                                            console.debug(
                                              'Failed to apply badge style',
                                              e
                                            );
                                          }
                                          badgeRefs.current[badge.id] = el;
                                        } else {
                                          delete badgeRefs.current[badge.id];
                                        }
                                      }}
                                    >
                                      {badge.image_url ? (
                                        <img
                                          src={
                                            buildAvatarUrl(badge.image_url) ||
                                            undefined
                                          }
                                          alt={badge.name}
                                          className="h-20 w-20 rounded-md object-cover"
                                        />
                                      ) : (
                                        <span className="text-5xl" aria-hidden>
                                          {getBadgeIcon(badge)}
                                        </span>
                                      )}
                                      <div className="flex-1">
                                        <p className="font-semibold text-white">
                                          {badge.name}
                                        </p>
                                        <p className="mt-1 text-xs text-white">
                                          {badge.description}
                                        </p>
                                        {earnedDate && (
                                          <div className="mt-2 flex items-center gap-2">
                                            <span className="inline-block rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                                              Earned
                                            </span>
                                            <span className="text-xs text-white">
                                              {earnedDate}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </button>
                                  );
                                })}

                                {lockedInCategory.map((badge) => (
                                  <button
                                    key={badge.id}
                                    type="button"
                                    onClick={() => {
                                      setSelectedBadge(badge);
                                      setSelectedUserBadge(null);
                                      setBadgeModalOpen(true);
                                    }}
                                    className="flex items-start gap-3 rounded-xl border border-l-4 bg-white p-4 text-left opacity-60"
                                    ref={(el) => {
                                      if (el) {
                                        // Do not color locked badges' borders — keep grey.
                                        badgeRefs.current[badge.id] = el;
                                      } else {
                                        delete badgeRefs.current[badge.id];
                                      }
                                    }}
                                  >
                                    {badge.image_url ? (
                                      <img
                                        src={
                                          buildAvatarUrl(badge.image_url) ||
                                          undefined
                                        }
                                        alt={badge.name}
                                        className="h-20 w-20 rounded-md object-cover shadow-sm grayscale"
                                      />
                                    ) : (
                                      <span
                                        className="text-5xl grayscale"
                                        aria-hidden
                                      >
                                        {getBadgeIcon(badge)}
                                      </span>
                                    )}
                                    <div className="flex-1">
                                      <p className="font-semibold text-gray-500">
                                        {badge.name}
                                      </p>
                                      <p className="mt-1 text-xs text-gray-500">
                                        {badge.description}
                                      </p>
                                      <p className="mt-2 text-xs font-medium text-gray-400">
                                        Not earned yet
                                      </p>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-8 text-center text-sm text-gray-600">
                      No badges available yet. Complete sessions to start
                      earning achievements!
                    </div>
                  )
                ) : // Owner viewing 'Unlocked' tab — show only earned badges grouped by category
                hasBadges ? (
                  <div className="space-y-6">
                    {orderedBadgeGroups.map(
                      ([categoryName, badges]: [
                        string,
                        BadgeWithCategory[],
                      ]) => {
                        const earnedInCategory = badges.filter((badge) =>
                          earnedBadgeIds.has(badge.id)
                        );

                        if (earnedInCategory.length === 0) return null;

                        return (
                          <div key={categoryName}>
                            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                              {categoryName}
                            </h4>
                            <div className="grid gap-3 sm:grid-cols-2">
                              {earnedInCategory.map((badge) => {
                                const userBadge = userBadges.find(
                                  (ub) => ub.badge_id === badge.id
                                );
                                const earnedDate = userBadge?.earned_at
                                  ? formatDateLabel(userBadge.earned_at)
                                  : null;

                                return (
                                  <button
                                    key={badge.id}
                                    type="button"
                                    onClick={() => {
                                      setSelectedBadge(badge);
                                      setSelectedUserBadge(userBadge ?? null);
                                      setBadgeModalOpen(true);
                                    }}
                                    className="flex transform items-start gap-3 rounded-xl border border-l-4 bg-white p-4 text-left shadow-lg ring-1 ring-gray-100 transition hover:scale-[1.02]"
                                    ref={(el) => {
                                      if (el) {
                                        try {
                                          el.style.setProperty(
                                            'border-left-color',
                                            badge.color ?? '',
                                            'important'
                                          );
                                          el.style.setProperty(
                                            'border-color',
                                            badge.color ?? '',
                                            'important'
                                          );
                                          el.style.setProperty(
                                            'background-color',
                                            badge.color ?? '',
                                            'important'
                                          );
                                        } catch (e) {
                                          console.debug(
                                            'Failed to apply badge style',
                                            e
                                          );
                                        }
                                        badgeRefs.current[badge.id] = el;
                                      } else {
                                        delete badgeRefs.current[badge.id];
                                      }
                                    }}
                                  >
                                    {badge.image_url ? (
                                      <img
                                        src={
                                          buildAvatarUrl(badge.image_url) ||
                                          undefined
                                        }
                                        alt={badge.name}
                                        className="h-20 w-20 rounded-md object-cover"
                                      />
                                    ) : (
                                      <span className="text-5xl" aria-hidden>
                                        {getBadgeIcon(badge)}
                                      </span>
                                    )}
                                    <div className="flex-1">
                                      <p className="font-semibold text-white">
                                        {badge.name}
                                      </p>
                                      <p className="mt-1 text-xs text-white">
                                        {badge.description}
                                      </p>
                                      {earnedDate && (
                                        <div className="mt-2 flex items-center gap-2">
                                          <span className="inline-block rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                                            Earned
                                          </span>
                                          <span className="text-xs text-white">
                                            {earnedDate}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-8 text-center text-sm text-gray-600">
                    No achievements earned yet.
                  </div>
                )
              ) : // Non-owner: only show earned badges grouped by category
              hasBadges ? (
                <div className="space-y-6">
                  {orderedBadgeGroups.map(
                    ([categoryName, badges]: [string, BadgeWithCategory[]]) => {
                      const earnedInCategory = badges.filter((badge) =>
                        earnedBadgeIds.has(badge.id)
                      );

                      if (earnedInCategory.length === 0) return null;

                      return (
                        <div key={categoryName}>
                          <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                            {categoryName}
                          </h4>
                          <div className="grid gap-3 sm:grid-cols-2">
                            {earnedInCategory.map((badge) => {
                              const userBadge = userBadges.find(
                                (ub) => ub.badge_id === badge.id
                              );
                              const earnedDate = userBadge?.earned_at
                                ? formatDateLabel(userBadge.earned_at)
                                : null;

                              return (
                                <button
                                  key={badge.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedBadge(badge);
                                    setSelectedUserBadge(userBadge ?? null);
                                    setBadgeModalOpen(true);
                                  }}
                                  className="flex transform items-start gap-3 rounded-xl border border-l-4 bg-white p-4 text-left shadow-lg ring-1 ring-gray-100 transition hover:scale-[1.02]"
                                  ref={(el) => {
                                    if (el) {
                                      try {
                                        el.style.setProperty(
                                          'border-left-color',
                                          badge.color ?? '',
                                          'important'
                                        );
                                        el.style.setProperty(
                                          'border-color',
                                          badge.color ?? '',
                                          'important'
                                        );
                                        el.style.setProperty(
                                          'background-color',
                                          badge.color ?? '',
                                          'important'
                                        );
                                        el.style.setProperty(
                                          'color',
                                          '#ffffff',
                                          'important'
                                        );
                                        el.style.setProperty(
                                          'color',
                                          '#ffffff',
                                          'important'
                                        );
                                      } catch (e) {
                                        console.debug(
                                          'Failed to apply badge style',
                                          e
                                        );
                                      }
                                      badgeRefs.current[badge.id] = el;
                                    } else {
                                      delete badgeRefs.current[badge.id];
                                    }
                                  }}
                                >
                                  {badge.image_url ? (
                                    <img
                                      src={
                                        buildAvatarUrl(badge.image_url) ||
                                        undefined
                                      }
                                      alt={badge.name}
                                      className="h-20 w-20 rounded-md object-cover"
                                    />
                                  ) : (
                                    <span className="text-5xl" aria-hidden>
                                      {getBadgeIcon(badge)}
                                    </span>
                                  )}
                                  <div className="flex-1">
                                    <p className="font-semibold text-white">
                                      {badge.name}
                                    </p>
                                    <p className="mt-1 text-xs text-white">
                                      {badge.description}
                                    </p>
                                    {earnedDate && (
                                      <div className="mt-2 flex items-center gap-2">
                                        <span className="inline-block rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                                          Earned
                                        </span>
                                        <span className="text-xs text-white">
                                          {earnedDate}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 px-6 py-8 text-center text-sm text-gray-600">
                  No achievements earned yet.
                </div>
              )}
            </section>
          </div>
        </div>
      )}
      {avatarModalOpen && avatarUrl && (
        <Modal title={displayName} onClose={() => setAvatarModalOpen(false)}>
          <div className="flex flex-col items-center gap-4">
            <img
              src={avatarUrl}
              alt={displayName}
              className="max-h-[65vh] w-full max-w-[90vw] rounded-3xl object-contain"
            />
            {profileSettings?.profile_bio && (
              <p className="whitespace-pre-line text-center text-sm text-gray-600">
                {profileSettings.profile_bio}
              </p>
            )}
          </div>
        </Modal>
      )}
      {badgeModalOpen && selectedBadge && (
        <Modal
          title={selectedBadge.name}
          onClose={() => {
            setBadgeModalOpen(false);
            setSelectedBadge(null);
            setSelectedUserBadge(null);
          }}
          style={
            selectedBadge.color
              ? { backgroundColor: selectedBadge.color, color: '#ffffff' }
              : undefined
          }
        >
          <div className="flex flex-col items-center gap-4">
            {selectedBadge.image_url ? (
              <img
                src={buildAvatarUrl(selectedBadge.image_url) || undefined}
                alt={selectedBadge.name}
                className="max-h-[65vh] w-full max-w-[520px] rounded-2xl object-contain"
              />
            ) : (
              <div className="flex h-48 w-48 items-center justify-center rounded-xl bg-gray-50 text-7xl">
                {getBadgeIcon(selectedBadge)}
              </div>
            )}

            <div className="w-full max-w-[520px]">
              <p className="text-lg font-semibold text-white">
                {selectedBadge.name}
              </p>
              {selectedBadge.description && (
                <p className="mt-2 text-sm text-white">
                  {selectedBadge.description}
                </p>
              )}
              {selectedUserBadge?.earned_at ? (
                <p className="mt-3 text-sm font-medium text-white">
                  Earned {formatDateLabel(selectedUserBadge.earned_at)}
                </p>
              ) : (
                <p className="mt-3 text-sm font-medium text-white">
                  Not earned yet
                </p>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Profile;
